"use client";

import cnBind from "classnames/bind";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";

import { AgentChatWidget } from "@/components/AgentChatWidget";
import { StickyLaunchCta } from "@/components/landing/StickyLaunchCta";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { PageHeadSync } from "@/components/PageHeadSync/PageHeadSync";
import { getNavbarItems } from "@/components/NavBar/constants";
import { PatternBackground } from "@/components/LiquidGlass";
import { HOME_PAGE_DESCRIPTION, HOME_PAGE_TITLE, defaultPublicRobots } from "@/shared/constants/seo";
import { AdminLayout } from "./AdminLayout";
import { CabinetLayout } from "./CabinetLayout";
import { PartnerCabinetLayout } from "./PartnerCabinetLayout";
import type { PageLayoutProps } from "./types";

import styles from "./PageLayout.module.scss";

const cx = cnBind.bind(styles);

const SEO_BY_PATH: Record<string, { title: string; description: string }> = {
    "/": {
        title: HOME_PAGE_TITLE,
        description: HOME_PAGE_DESCRIPTION,
    },
    "/stati": {
        title: "Новости и статьи Tezam",
        description: "Экспертиза Tezam: аналитика и практические материалы по digital-продуктам.",
    },
    "/kontakty": {
        title: "Контакты Tezam",
        description: "Свяжитесь с командой Tezam для обсуждения Telegram mini app, SaaS или мобильного проекта.",
    },
    "/o-kompanii": {
        title: "О компании Tezam | Команда разработки SaaS и Telegram Mini Apps",
        description:
            "Узнайте больше об ИТ-компании полного цикла Tezam. Наша команда создает высоконагруженные облачные платформы, кастомные CRM-системы и Mini App интерфейсы для автоматизации и масштабирования вашего бизнеса.",
    },
    "/istorya-kompanii": {
        title: "О компании Tezam | Команда разработки SaaS и Telegram Mini Apps",
        description:
            "Узнайте больше об ИТ-компании полного цикла Tezam. Наша команда создает высоконагруженные облачные платформы, кастомные CRM-системы и Mini App интерфейсы для автоматизации и масштабирования вашего бизнеса.",
    },
    "/portfolio": {
        title: "Портфолио Tezam",
        description: "Реальные проекты Tezam: Telegram mini apps, SaaS, мобильные и веб-продукты.",
    },
    "/knowledge": {
        title: "База знаний TEZAM — справочник CRM, Mini App и сайта",
        description:
            "Индекс справочника TEZAM: организация, услуги, товары, операции, CRM, финансы и аналитика.",
    },
    "/tariffs": {
        title: "Тарифы TEZAM — подписка, которая окупается с первого клиента",
        description:
            "Бизнес и Про: Mini App, CRM и сайт. Месяц или год со скидкой. Спецтариф Альфа Банк 50 BYN.",
    },
    "/razrabotka-telegram-mini-app": {
        title: "Разработка Telegram Mini App под ключ",
        description: "Проектируем и запускаем Telegram Mini App: UX, разработка, интеграции и поддержка.",
    },
    "/frontend-backend-razrabotka": {
        title: "Frontend и Backend разработка",
        description: "Fullstack-разработка веб-продуктов: надежный backend, быстрый frontend и интеграции.",
    },
    "/razrabotka-saas-produktov": {
        title: "Разработка SaaS-продуктов",
        description: "Создаем масштабируемые SaaS-платформы: от MVP до зрелого продукта.",
    },
    "/ui-ux-dizajn-i-prototipirovanie": {
        title: "UI/UX дизайн и прототипирование",
        description: "Проектируем интерфейсы и пользовательские сценарии для роста конверсии и удобства.",
    },
    "/avtomatizacija-i-biznes-analitika": {
        title: "Автоматизация и бизнес-аналитика",
        description: "Автоматизируем процессы и внедряем аналитические инструменты для управляемого роста.",
    },
    "/terms": {
        title: "Пользовательское соглашение | Tezam",
        description: "Условия использования сервиса Tezam: права, обязанности и правила взаимодействия.",
    },
    "/politika-konfidentsialnosti": {
        title: "Политика конфиденциальности | Tezam",
        description: "Порядок обработки и защиты персональных данных в сервисах Tezam.",
    },
    "/oplata-i-poluchenie-uslugi": {
        title: "Оплата и получение услуги | Tezam",
        description:
            "Условия оплаты подписки Tezam, сроки активации и способ получения доступа к сервису после оплаты.",
    },
    "/auth": {
        title: "Аккаунт | Tezam",
        description: "Вход, регистрация и восстановление доступа Tezam.",
    },
    "/cabinet": {
        title: "Сервисы | Кабинет Tezam",
        description: "Mini App и CRM клиента Tezam.",
    },
    "/cabinet/create": {
        title: "Новый сервис | Tezam",
        description: "Создайте Mini App и CRM в личном кабинете Tezam.",
    },
    "/cabinet/publish": {
        title: "Каталог | Кабинет Tezam",
        description: "Ваши кейсы в каталоге Tezam: статусы, модерация и публикация.",
    },
    "/cabinet/billing": {
        title: "Счета и оплата | Кабинет Tezam",
        description: "Тарифы и модули по сервисам, оплата картой или Telegram Stars.",
    },
    "/cabinet/company": {
        title: "Реквизиты | Кабинет Tezam",
        description: "Данные для договоров и счетов Tezam.",
    },
    "/cabinet/profile": {
        title: "Профиль | Кабинет Tezam",
        description: "Контакты и аккаунт клиента Tezam.",
    },
    "/cabinet/partner": {
        title: "Партнёрам | Кабинет Tezam",
        description: "Рефералы и промокоды партнёра Tezam.",
    },
    "/cabinet/notifications": {
        title: "Мои уведомления | Кабинет Tezam",
        description: "Входящие уведомления аккаунта Tezam.",
    },
    "/login": {
        title: "Вход | Tezam",
        description: "Вход в личный кабинет Tezam.",
    },
    "/register": {
        title: "Регистрация | Tezam",
        description: "Регистрация клиента Tezam.",
    },
    "/verify-email": {
        title: "Подтверждение email | Tezam",
        description: "Подтверждение адреса электронной почты.",
    },
    "/invite": {
        title: "Приглашение | Tezam",
        description: "Установка пароля по приглашению в Tezam.",
    },
    "/forgot-password": {
        title: "Восстановление пароля | Tezam",
        description: "Сброс пароля в Tezam.",
    },
    "/reset-password": {
        title: "Новый пароль | Tezam",
        description: "Установка нового пароля в Tezam.",
    },
};

const getSeoFallback = (pathname: string) => {
    if (SEO_BY_PATH[pathname]) return SEO_BY_PATH[pathname];
    if (pathname.startsWith("/stati/")) {
        return {
            title: "Статья Tezam",
            description: "Материал Tezam о разработке цифровых продуктов, Telegram mini apps и SaaS.",
        };
    }
    if (pathname.startsWith("/portfolio/")) {
        return {
            title: "Кейс Tezam",
            description: "Подробный кейс Tezam: задача, решение, стек и результат для бизнеса.",
        };
    }
    if (pathname.startsWith("/razrabotka-telegram-mini-app/")) return SEO_BY_PATH["/razrabotka-telegram-mini-app"];
    if (pathname.startsWith("/frontend-backend-razrabotka/")) return SEO_BY_PATH["/frontend-backend-razrabotka"];
    if (pathname.startsWith("/razrabotka-saas-produktov/")) return SEO_BY_PATH["/razrabotka-saas-produktov"];
    if (pathname.startsWith("/ui-ux-dizajn-i-prototipirovanie/")) return SEO_BY_PATH["/ui-ux-dizajn-i-prototipirovanie"];
    if (pathname.startsWith("/avtomatizacija-i-biznes-analitika/")) return SEO_BY_PATH["/avtomatizacija-i-biznes-analitika"];
    return {
        title: "Tezam - цифровые решения для бизнеса",
        description: "Команда Tezam разрабатывает Telegram mini apps, SaaS, mobile и web-решения.",
    };
};

export const PageLayout = ({ children, title, description, robots }: PageLayoutProps) => {
    const pathname = usePathname() ?? "/";
    const isAdmin = pathname.startsWith("/admin");
    const isCabinet = pathname.startsWith("/cabinet");
    const isPartnerCabinet = pathname.startsWith("/partners/cabinet");
    const { t } = useTranslation("common");
    const seoFallback = getSeoFallback(pathname);
    const defaultTitle = seoFallback.title;
    const defaultDescription = seoFallback.description;
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://tezam.by";
    const rawCanonicalPath = pathname.split("#")[0].split("?")[0] || "/";
    const canonicalPath = rawCanonicalPath.length > 1 ? rawCanonicalPath.replace(/\/+$/, "") : rawCanonicalPath;
    const canonicalUrl = new URL(canonicalPath, siteUrl).toString();
    const defaultOgImage = new URL("/favicon.svg", siteUrl).toString();
    const ogImage = process.env.NEXT_PUBLIC_OG_IMAGE_URL ?? defaultOgImage;
    const effectiveTitle = title || defaultTitle;
    const effectiveDescription = description || defaultDescription;

    if (isAdmin) {
        return (
            <AdminLayout title={title} pathname={pathname}>
                {children}
            </AdminLayout>
        );
    }
    if (isPartnerCabinet) {
        return (
            <PartnerCabinetLayout title={title} description={description}>
                {children}
            </PartnerCabinetLayout>
        );
    }
    if (isCabinet) {
        return (
            <CabinetLayout title={title} description={description}>
                {children}
            </CabinetLayout>
        );
    }
    const data = getNavbarItems(t);
    const isHome = pathname === "/" || pathname === "";
    // Home sells the product; industry presets live on /solutions/* (no fixed preset bar).
    return (
        <>
            <PageHeadSync
                title={effectiveTitle}
                description={effectiveDescription}
                canonicalUrl={canonicalUrl}
                ogImage={ogImage}
                robots={robots || defaultPublicRobots()}
            />
            <div className={cx("wrapper")}>
                {/* Full-page grid competes with hero on home — keep pattern on inner pages. */}
                {isHome ? null : <PatternBackground />}
                <Header data={data} />
                <main className={cx("main")}>
                    <div className={cx("content")} key={pathname}>
                        {children}
                    </div>
                </main>
                <Footer />
                <StickyLaunchCta />
                <AgentChatWidget />
                <CookieConsentBanner />
            </div>
        </>
    );
};
